// Pull-to-load next page functionality for search results

import { isFulltextTabActive } from './utils.js';
import { 
    getPullToLoadState,
    setPullToLoadState,
    setTargetDistance,
    setCurrentDisplayDistance,
    getAnimationFrameId,
    setAnimationFrameId
} from './state.js';
import { handleWheel, handleScroll, handleTouchStart, handleTouchMove, handleTouchEnd } from './event-handlers.js';
import { removePullIndicator } from './cleanup.js';

/**
 * Initialize pull-to-load functionality
 * @param {Object} pagination - Current pagination data
 * @param {string} query - Current search query
 */
export function initPullToLoad(pagination, query) {
    const container = document.getElementById('nav-full-text');
    if (!container) return;
    
    const resultsContainer = container.querySelector('#fulltext-results-container');
    if (!resultsContainer) return;
    
    // Only initialize if fulltext tab is active
    if (!isFulltextTabActive()) {
        // Clean up if tab is not active
        removePullIndicator();
        return;
    }
    
    // Update state
    setPullToLoadState({
        currentPage: pagination?.current_page || 1,
        totalPages: pagination?.total_pages || 1,
        query: query || ''
    });
    
    // Reset animation state
    setTargetDistance(0);
    setCurrentDisplayDistance(0);
    const animationFrameId = getAnimationFrameId();
    if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
        setAnimationFrameId(null);
    }
    
    // DON'T clear justFinishedLoading flag here - it's managed by loadNextPage
    // This prevents text flickering when initPullToLoad is called after page load
    
    // Check if we can load more pages
    const state = getPullToLoadState();
    const canLoadMore = state.currentPage < state.totalPages;
    if (!canLoadMore) {
        removePullIndicator();
        return;
    }
    
    // Remove old listeners from window (for wheel events and touch)
    window.removeEventListener('wheel', handleWheel, { passive: false });
    window.removeEventListener('scroll', handleScroll);
    window.removeEventListener('touchstart', handleTouchStart, { passive: true });
    window.removeEventListener('touchmove', handleTouchMove, { passive: false });
    window.removeEventListener('touchend', handleTouchEnd, { passive: true });
    
    // Remove visualViewport listeners if they exist
    if (window.visualViewport) {
        window.visualViewport.removeEventListener('scroll', handleScroll);
        window.visualViewport.removeEventListener('resize', handleScroll);
    }
    
    // Add new listeners
    window.addEventListener('wheel', handleWheel, { passive: false });
    window.addEventListener('scroll', handleScroll, { passive: true });
    // Touch events for iOS/mobile devices
    // Use passive: true for touchstart to allow normal scrolling
    // preventDefault will be called in touchmove only when actually pulling down
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    
    // Add visualViewport listeners for mobile browser UI changes
    // These fire when browser UI shows/hides, allowing us to react immediately
    if (window.visualViewport) {
        window.visualViewport.addEventListener('scroll', handleScroll, { passive: true });
        window.visualViewport.addEventListener('resize', handleScroll, { passive: true });
    }
}

// Re-export cleanup function
export { cleanupPullToLoad } from './cleanup.js';

